import { useEffect, useState } from "react";
import { Plus, Loader2, Pencil, Search, Wallet } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtDate, fmtGram } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const empty = {
  company_name: "", contact_person: "", phone: "", email: "",
  password: "", customer_code: "", address: "", active: true, has_limit: "", milyem_pct: "", category_milyem_pct: {}, hide_milyem: false,
};

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [cats, setCats] = useState([]);

  const [balOpen, setBalOpen] = useState(false);
  const [balCust, setBalCust] = useState(null);
  const [balData, setBalData] = useState({ balance_has: 0, entries: [] });
  const [balForm, setBalForm] = useState({ kind: "Ödeme", amount_has: "", description: "" });
  const [balSaving, setBalSaving] = useState(false);

  const openBalance = async (c) => {
    setBalCust(c);
    setBalForm({ kind: "Ödeme", amount_has: "", description: "" });
    setBalData({ balance_has: 0, entries: [] });
    setBalOpen(true);
    try {
      const r = await api.get(`/admin/customers/${c.id}/balance`);
      setBalData(r.data);
    } catch {}
  };

  const addBalance = async () => {
    const amt = parseFloat(balForm.amount_has);
    if (!amt || amt <= 0) { toast.error("Geçerli bir has altın miktarı girin"); return; }
    setBalSaving(true);
    try {
      await api.post(`/admin/customers/${balCust.id}/balance`, {
        kind: balForm.kind, amount_has: amt, description: balForm.description,
      });
      const r = await api.get(`/admin/customers/${balCust.id}/balance`);
      setBalData(r.data);
      setBalForm({ kind: "Ödeme", amount_has: "", description: "" });
      toast.success("Bakiye hareketi eklendi");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBalSaving(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/customers");
      setCustomers(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const openNew = () => { setEditing(null); setForm({ ...empty, category_milyem_pct: {} }); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...empty, ...c, category_milyem_pct: c.category_milyem_pct || {}, password: "" });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCatPct = (cat, v) => setForm((f) => ({ ...f, category_milyem_pct: { ...(f.category_milyem_pct || {}), [cat]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const cmp = {};
      Object.entries(form.category_milyem_pct || {}).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined && !isNaN(Number(v))) cmp[k] = Number(v);
      });
      const base = {
        ...form,
        has_limit: form.has_limit === "" ? 0 : Number(form.has_limit),
        milyem_pct: form.milyem_pct === "" ? 0 : Number(form.milyem_pct),
        category_milyem_pct: cmp,
        hide_milyem: !!form.hide_milyem,
      };
      if (editing) {
        const payload = { ...base };
        if (!payload.password) delete payload.password;
        await api.put(`/admin/customers/${editing.id}`, payload);
        toast.success("Müşteri güncellendi");
      } else {
        await api.post("/admin/customers", base);
        toast.success("Müşteri oluşturuldu");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const filtered = customers.filter((c) =>
    !q || [c.company_name, c.contact_person, c.email, c.customer_code].some((x) => (x || "").toLowerCase().includes(q.toLowerCase()))
  );

  const inputCls = "bg-obsidian border-border text-foreground focus-visible:ring-gold";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl text-foreground">Müşteriler</h1>
          <p className="text-sm text-muted-foreground mt-1">{customers.length} müşteri hesabı</p>
        </div>
        <button data-testid="add-customer-button" onClick={openNew} className="flex items-center gap-2 px-4 h-10 bg-gold text-obsidian font-semibold rounded-md hover:bg-gold-hover transition-colors">
          <Plus size={18} /> Müşteri Ekle
        </button>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Müşteri ara..." className={`pl-9 ${inputCls}`} data-testid="customer-search" />
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-gold" size={28} /></div>
      ) : (
        <div className="bg-surface border border-border rounded-md overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Firma</th>
                <th className="px-4 py-3 font-medium">Yetkili</th>
                <th className="px-4 py-3 font-medium">Telefon</th>
                <th className="px-4 py-3 font-medium">Kod</th>
                <th className="px-4 py-3 font-medium">Has Limiti</th>
                <th className="px-4 py-3 font-medium">Milyem %</th>
                <th className="px-4 py-3 font-medium">Durum</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.customer_code}`} className="border-b border-border last:border-0 hover:bg-elevated/50">
                  <td className="px-4 py-3 text-foreground">{c.company_name}<div className="text-[11px] text-muted-foreground">{c.email}</div></td>
                  <td className="px-4 py-3 text-muted-foreground">{c.contact_person}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.phone}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{c.customer_code}</td>
                  <td className="px-4 py-3 text-muted-foreground" data-testid={`customer-limit-${c.customer_code}`}>{c.has_limit > 0 ? <span className="text-gold">{fmtGram(c.has_limit)} gr</span> : <span className="text-muted-foreground">Sınırsız</span>}</td>
                  <td className="px-4 py-3 text-muted-foreground" data-testid={`customer-milyem-${c.customer_code}`}>{(() => {
                    const g = c.milyem_pct; const hasCat = c.category_milyem_pct && Object.keys(c.category_milyem_pct).length;
                    if (!g && !hasCat) return <span className="text-muted-foreground">Ürün milyemi</span>;
                    return <span className="text-gold">{g ? (g > 0 ? "+" : "") + g + "%" : "Ürün"}{hasCat ? " · kat." : ""}</span>;
                  })()}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] px-2 py-1 rounded border ${c.active ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-red-400 border-red-500/30 bg-red-500/10"}`}>
                      {c.active ? "Aktif" : "Pasif"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button data-testid={`balance-customer-${c.customer_code}`} onClick={() => openBalance(c)} className="text-muted-foreground hover:text-gold transition-colors p-1" title="Bakiye"><Wallet size={16} /></button>
                    <button data-testid={`edit-customer-${c.customer_code}`} onClick={() => openEdit(c)} className="text-muted-foreground hover:text-gold transition-colors p-1 ml-1"><Pencil size={16} /></button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Müşteri bulunamadı.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-border text-foreground max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Müşteri Düzenle" : "Yeni Müşteri"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Firma Adı" span><Input data-testid="cf-company" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className={inputCls} /></Field>
            <Field label="Yetkili Kişi"><Input data-testid="cf-contact" value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} className={inputCls} /></Field>
            <Field label="Telefon"><Input data-testid="cf-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} /></Field>
            <Field label="E-posta"><Input data-testid="cf-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} /></Field>
            <Field label="Müşteri Kodu"><Input data-testid="cf-code" value={form.customer_code} onChange={(e) => set("customer_code", e.target.value)} className={inputCls} /></Field>
            <Field label="Has Limiti (gr) · 0 = sınırsız"><Input data-testid="cf-haslimit" type="number" step="0.001" value={form.has_limit} onChange={(e) => set("has_limit", e.target.value)} placeholder="0" className={inputCls} /></Field>
            <Field label="Milyem Farkı (%) · Varsayılan · +/- olabilir"><Input data-testid="cf-milyem" type="number" step="0.1" value={form.milyem_pct} onChange={(e) => set("milyem_pct", e.target.value)} placeholder="0" className={inputCls} /></Field>
            <Field label={editing ? "Yeni Şifre (boş bırakılabilir)" : "Şifre"} span><Input data-testid="cf-password" type="text" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder={editing ? "Değiştirmek için yazın" : ""} className={inputCls} /></Field>
            <Field label="Adres" span><Textarea data-testid="cf-address" value={form.address} onChange={(e) => set("address", e.target.value)} className={inputCls} /></Field>
            {cats.length > 0 && (
              <div className="col-span-2" data-testid="cf-catpct-section">
                <div className="text-xs text-muted-foreground mb-2">Kategori bazlı milyem farkı (%) · boş = varsayılan uygulanır</div>
                <div className="grid grid-cols-2 gap-2 max-h-44 overflow-y-auto pr-1">
                  {cats.map((c) => (
                    <div key={c.id} className="flex items-center gap-2 bg-obsidian border border-border rounded-md px-2 py-1">
                      <span className="text-xs text-foreground flex-1 truncate">{c.name}</span>
                      <Input data-testid={`cf-catpct-${c.name}`} type="number" step="0.1" value={form.category_milyem_pct?.[c.name] ?? ""} onChange={(e) => setCatPct(c.name, e.target.value)} placeholder="—" className={`${inputCls} w-20 h-8`} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="col-span-2 flex items-center justify-between bg-obsidian border border-border rounded-md px-3 py-2.5">
              <span className="text-sm">Aktif Hesap</span>
              <Switch data-testid="cf-active" checked={form.active} onCheckedChange={(v) => set("active", v)} />
            </div>
            <div className="col-span-2 flex items-center justify-between bg-obsidian border border-border rounded-md px-3 py-2.5">
              <span className="text-sm">Milyem'i Gizle <span className="text-xs text-muted-foreground">(müşteri milyem değerlerini görmez)</span></span>
              <Switch data-testid="cf-hidemilyem" checked={!!form.hide_milyem} onCheckedChange={(v) => set("hide_milyem", v)} />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 h-10 border border-border rounded-md text-muted-foreground hover:text-foreground transition-colors">İptal</button>
            <button data-testid="save-customer-button" onClick={save} disabled={saving} className="px-5 h-10 bg-gold text-obsidian font-semibold rounded-md hover:bg-gold-hover transition-colors flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="animate-spin" size={16} />} Kaydet
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={balOpen} onOpenChange={setBalOpen}>
        <DialogContent className="bg-surface border-border text-foreground max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Bakiye · {balCust?.company_name}</DialogTitle>
          </DialogHeader>

          <div className="bg-obsidian border border-border rounded-md p-4 mb-2">
            <div className="text-[10px] tracking-luxe uppercase text-gold mb-1">Güncel Has Altın Bakiyesi</div>
            <div data-testid="admin-balance-amount" className={`text-3xl font-semibold ${balData.balance_has > 0 ? "text-gold" : "text-emerald-400"}`}>
              {fmtGram(balData.balance_has)} gr
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              {balData.balance_has > 0 ? "Müşterinin ORSE GOLD'a borcu" : balData.balance_has < 0 ? "Müşteri lehine bakiye" : "Bakiye sıfır"}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-muted-foreground mb-1 block">Hareket Tipi</label>
              <Select value={balForm.kind} onValueChange={(v) => setBalForm((f) => ({ ...f, kind: v }))}>
                <SelectTrigger data-testid="balance-kind" className="bg-obsidian border-border text-foreground h-9 focus:ring-gold"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface border-border text-foreground">
                  <SelectItem value="Ödeme">Ödeme (bakiye azalır)</SelectItem>
                  <SelectItem value="Borç">Borç (bakiye artar)</SelectItem>
                  <SelectItem value="Düzeltme">Düzeltme (artır)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground mb-1 block">Has Altın (gr)</label>
              <Input data-testid="balance-amount" type="number" step="0.001" value={balForm.amount_has} onChange={(e) => setBalForm((f) => ({ ...f, amount_has: e.target.value }))} className="bg-obsidian border-border text-foreground h-9 focus-visible:ring-gold" />
            </div>
            <div className="col-span-2">
              <label className="text-[11px] text-muted-foreground mb-1 block">Açıklama</label>
              <Input data-testid="balance-desc" value={balForm.description} onChange={(e) => setBalForm((f) => ({ ...f, description: e.target.value }))} placeholder="Örn. Nakit tahsilat / altın teslimi" className="bg-obsidian border-border text-foreground h-9 focus-visible:ring-gold" />
            </div>
          </div>
          <button data-testid="save-balance-button" onClick={addBalance} disabled={balSaving} className="w-full h-10 mt-1 bg-gold text-obsidian font-semibold rounded-md hover:bg-gold-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {balSaving && <Loader2 className="animate-spin" size={16} />} Hareket Ekle
          </button>

          {balData.entries?.length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] tracking-luxe uppercase text-muted-foreground mb-2">Hesap Ekstresi</div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {balData.entries.map((e) => (
                  <div key={e.id} className="flex items-center justify-between text-sm border-b border-border py-1.5 last:border-0">
                    <div className="min-w-0">
                      <div className="text-foreground">{e.kind}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{e.description} · {fmtDate(e.created_at)}</div>
                    </div>
                    <span className={`font-medium ${e.delta_has >= 0 ? "text-gold" : "text-emerald-400"}`}>{e.delta_has >= 0 ? "+" : ""}{fmtGram(e.delta_has)} gr</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, span, children }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <label className="text-[11px] text-muted-foreground mb-1 block">{label}</label>
      {children}
    </div>
  );
}
